import type { ComboboxItemGroup } from '@any-ui-react/core';
import { Checkbox, Group, useCombobox } from '@any-ui-react/core';
import classNames from 'classnames';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ComboboxWrapper } from './ComboboxWrapper';
import { findInData, isGroup } from './group';
import type { ComboboxItemWithFormatted, SelectCommonProps, SelectDataProps } from './props';

export interface SelectMultipleProps<TData extends ComboboxItemWithFormatted<TValue>, TValue extends string = string>
  extends SelectCommonProps {
  filter?: (value: TData) => boolean;
  multipleSelectedLabel?: (length: number, labels: Array<string>) => string;
  value: Array<string> | null | undefined;
  onChange?: (value: Array<TData>) => void;
  onOptionsCountChange?: (count: number) => void;
}

export const SelectMultiple = <TData extends ComboboxItemWithFormatted<TValue>, TValue extends string = string>({
  clearable = true,
  data,
  filter,
  loading,
  multipleSelectedLabel,
  value,
  onChange,
  onOptionsCountChange,
  onSearchChange,
  inputRef,
  ...props
}: SelectMultipleProps<TData> & SelectDataProps<TData>) => {
  /**
   * `data` can be a flat item array or a grouped one (`{ group, items }[]`, e.g.
   * `CompanySelectMultiple` grouping companies by country) - `data.length`/`data.filter(filter)`
   * would silently treat each GROUP as a single option in the latter case (undercounting, and
   * running `filter` against the group object instead of its items - `item.value` is `undefined`
   * there, so any non-trivial allowlist filter would incorrectly drop the whole group). Mirrors
   * `findInData`'s `isGroup` traversal so grouped and flat data behave identically here.
   */
  const filteredData = useMemo(() => {
    if (!filter) return data;

    return data.reduce<TData[]>((acc, item) => {
      if (isGroup(item)) {
        const group = item as unknown as ComboboxItemGroup;
        const filteredItems = group.items.filter(sub => typeof sub !== 'string' && filter(sub as unknown as TData));
        if (filteredItems.length > 0) acc.push({ ...group, items: filteredItems } as unknown as TData);
        return acc;
      }

      if (filter(item)) acc.push(item);
      return acc;
    }, []);
  }, [data, filter]);

  const optionsCount = useMemo(
    () =>
      filteredData.reduce(
        (count, item) => count + (isGroup(item) ? (item as unknown as ComboboxItemGroup).items.length : 1),
        0,
      ),
    [filteredData],
  );
  const onOptionsCountChangeRef = useRef(onOptionsCountChange);
  onOptionsCountChangeRef.current = onOptionsCountChange;

  useEffect(() => {
    onOptionsCountChangeRef.current?.(optionsCount);
  }, [optionsCount]);
  const [internalValue, setInternalValue] = useState<string[]>(value ?? []);
  const [searchValue, setSearchValue] = useState('');
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearchValue('');
    },
  });

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const textValue = useMemo(() => {
    if (isEmpty(internalValue) || loading) {
      return '';
    } else if (internalValue.length === 1) {
      return findInData(data, internalValue[0])?.label ?? '';
    } else if (multipleSelectedLabel) {
      return multipleSelectedLabel(
        internalValue.length,
        internalValue.map(v => findInData(data, v)?.label).filter((l): l is string => l != null),
      );
    }

    return `${internalValue.length} selected`;
  }, [data, internalValue, loading, multipleSelectedLabel]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: excludes `internalValue` to avoid overwriting user input when `handleValueSelect` is triggered
  useEffect(() => {
    if (!isEqual(value ?? [], internalValue)) {
      setInternalValue(value ?? []);
    }
  }, [value]);

  useEffect(() => {
    if (inputRef) inputRef.current = combobox.searchRef.current;
  });

  useEffect(() => {
    if (combobox.dropdownOpened && onSearchChange && combobox.searchRef.current) {
      combobox.focusSearchInput();
    }
  }, [combobox, onSearchChange]);

  const handleValueSelect = useCallback((value: string) => {
    setInternalValue(current =>
      !current ? [value] : current.includes(value) ? current.filter(v => v !== value) : [...current, value],
    );
  }, []);

  const onClickRightSection = useCallback(() => {
    if (clearable && !isEmpty(internalValue)) {
      setInternalValue([]);
      onChange?.([]);
    } else {
      combobox.toggleDropdown('mouse');
    }
  }, [clearable, combobox, internalValue, onChange]);

  const wasDropdownOpened = useRef(combobox.dropdownOpened);
  const internalValueRef = useRef(internalValue);
  internalValueRef.current = internalValue;

  // biome-ignore lint/correctness/useExhaustiveDependencies: excludes `data`, `onChange` to avoid infinite loop - use ref to always call latest onChange
  useEffect(() => {
    // Apply values only on an actual open -> closed transition (dropdown-driven selection),
    // not merely because `internalValue` was synced from an externally-set `value` prop while
    // already closed (e.g. a selection made via EmployeeSearchPopup, bypassing this dropdown
    // entirely) - otherwise this would try to re-resolve that value against this combobox's own
    // (possibly empty) `data` and silently wipe it back out via onChange([]).
    const justClosed = wasDropdownOpened.current && !combobox.dropdownOpened;
    wasDropdownOpened.current = combobox.dropdownOpened;
    if (!justClosed) return;

    const option = internalValueRef.current.map(v => findInData(data, v)).filter((it): it is TData => it != null);
    onChangeRef.current?.(option);
  }, [combobox.dropdownOpened]);

  return (
    <ComboboxWrapper
      {...props}
      clearable={clearable}
      combobox={combobox}
      data={filteredData}
      handleValueSelect={handleValueSelect}
      internalValue={internalValue}
      isActive={it => internalValue.includes(it)}
      loading={loading}
      refOptions={null}
      renderOption={(item, active) => (
        <Group className="flex flex-nowrap gap-2 items-start">
          <Checkbox aria-hidden="true" checked={active} className="mt-[3px]" tabIndex={-1} onChange={() => {}} />
          <span className={classNames('text-sm flex-1', { 'text-gray-6': item.disabled, 'text-blue-6': active })}>
            {item.labelFormatted ?? item.label}
          </span>
        </Group>
      )}
      searchRef={combobox.searchRef}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      textValue={textValue}
      onClickRightSection={onClickRightSection}
      onSearchChange={onSearchChange}
    />
  );
};
